import formatcoords from "formatcoords"
import parseDms from "parse-dms"
import config from "config/config"
import i18n from "intl/i18n"

import { Coordinates } from "@/types"
import Address from "models/Address"
import { MapType } from "config/constants"
import { CityProvenance } from "utils/addressAutofill"

export interface IGeocodeAddressComponent {
    long_name: string
    short_name: string
    types: string[]
}
interface IGeocodeResult {
    geometry: { location: { lat: number, lng: number } }
    address_components: IGeocodeAddressComponent[]
}
interface IGeocodeResponse {
    results: IGeocodeResult[]
    status: string
}

// Direct call to the Google Geocoding REST API (what react-geocode wrapped).
// Kept as REST rather than google.maps.Geocoder so geocoding stays decoupled
// from the Maps JS SDK being loaded — reverse geocoding is called from places
// without a map (ShowAddressButton, delivery step, warehouse modals).
async function geocode(address: string): Promise<IGeocodeResponse> {
    const params = new URLSearchParams({
        address,
        key: config.googleMapsApiKey,
        language: i18n.language,
    })
    const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?${params.toString()}`)
    return response.json()
}

const CITY_SUBDIVISION_TYPES = [
    "postal_town",
    "sublocality_level_1",
    "sublocality",
    "administrative_area_level_3",
    "administrative_area_level_2",
    "administrative_area_level_1",
]

/**
 * Resolves the City field from geocoding address components.
 *
 * Prefers the canonical `locality`. When it is absent and
 * `fillMissingCityFromSubdivision` is set, falls back to the most town-like
 * component available, in decreasing specificity: `postal_town` →
 * `sublocality`(_level_1) → `administrative_area_level_3` → `_level_2` →
 * `_level_1`. Town-level types deliberately rank above the broader district
 * (`administrative_area_level_2`) so a village like Großarl is not labelled
 * with its district ("Sankt Johann im Pongau"); the low ranks still recover a
 * usable City where Google returns nothing finer (e.g. Hong Kong, where
 * `administrative_area_level_1` is the meaningful value).
 *
 * The caller passes the components flattened across all geocoding results, so
 * a `postal_town` present only in a sibling result is still found.
 *
 * @param components Address components (typically flattened across results).
 * @param options    `fillMissingCityFromSubdivision` enables the fallback; off
 *                   by default so other callers are unaffected.
 * @returns The resolved city (or undefined) and where it originated.
 */
export function resolveGeocodedCity(
    components: IGeocodeAddressComponent[],
    options?: { fillMissingCityFromSubdivision?: boolean }
): { city: string | undefined, provenance: CityProvenance } {
    const byType = (type: string) => components.find(ac => ac.types.indexOf(type) > -1)?.long_name

    const locality = byType("locality")
    if (locality) {
        return { city: locality, provenance: "locality" }
    }

    if (options?.fillMissingCityFromSubdivision) {
        for (const type of CITY_SUBDIVISION_TYPES) {
            const value = byType(type)
            if (value) {
                return { city: value, provenance: "subdivision" }
            }
        }
    }

    return { city: undefined, provenance: "none" }
}

const UNITED_STATES_COUNTRY_CODE = "US"

/**
 * Tells whether a geocoded address is in the United States.
 *
 * Keys off the ISO 3166-1 alpha-2 code in the `country` component's
 * `short_name`, never its `long_name`: the geocoder is called with
 * `language: i18n.language`, so the country name comes back in the creating
 * user's browser language and cannot be compared against a fixed value.
 *
 * The comparison is a strict positive match against the two-letter code, so a
 * localized, long-form or absent value falls through to the behaviour used for
 * every other country instead of being formatted as an American address.
 *
 * @param country The `country` address component, if the result carries one.
 * @returns True when the resolved country is the United States.
 */
export function isUnitedStatesCountry(country: IGeocodeAddressComponent | undefined): boolean {
    return country?.short_name === UNITED_STATES_COUNTRY_CODE
}

/**
 * Appends the US state code to a resolved city, as `"{city}, {state}"`.
 *
 * The state is the `short_name` of `administrative_area_level_1`, which for US
 * addresses is the two-letter postal abbreviation the geocoder already
 * supplies — no state table is involved. The suffix is appended to every city
 * value, including one recovered from a subdivision, because the rule is
 * "city, state" without exception; a city that is itself the state therefore
 * comes out as `"Iowa, IA"`, which is intended and not a defect.
 *
 * A city that could not be resolved is returned unchanged, so the caller's
 * mandatory-field policy still sees an empty City and can substitute the
 * country as before.
 *
 * @param city       City resolved from the geocoding components.
 * @param components Address components of the geocoding result.
 * @returns The city with the state appended, or the city unchanged when either
 *          part is missing.
 */
export function appendUnitedStatesStateCode(
    city: string | undefined,
    components: IGeocodeAddressComponent[]
): string | undefined {
    if (!city) {
        return city
    }

    const stateCode = components.find(ac => ac.types.indexOf("administrative_area_level_1") > -1)?.short_name

    return [city, stateCode].filter(Boolean).join(", ")
}

/**
 * Builds the Street value from the geocoded route and house number.
 *
 * US addresses put the house number in front of the route
 * (`"123 Main Street"`), everywhere else it follows (`"Main Street 123"`).
 * A missing house number leaves the bare route with no stray separator, and a
 * house number without a route yields no street at all — unchanged from
 * before the US order existed, in both cases.
 *
 * @param streetName        `route` long name, or an empty string.
 * @param streetNumber      `street_number` long name, or an empty string.
 * @param unitedStatesOrder Whether to emit the US house-number-first order.
 * @returns The assembled street, or undefined when there is no route.
 */
export function formatGeocodedStreet(
    streetName: string,
    streetNumber: string,
    unitedStatesOrder: boolean
): string | undefined {
    if (!streetName) {
        return undefined
    }

    const parts = unitedStatesOrder ? [streetNumber, streetName] : [streetName, streetNumber]

    return parts.join(" ").trim() || undefined
}

export default class LocationService {
    static async getCoordinates(query: string): Promise<Coordinates | null> {
        const trimmedQuery = query.trim()

        if (/^([-+]?)([\d]{1,2})(((\.)(\d+)(,?)))(\s+)(([-+]?)([\d]{1,3})((\.)(\d+))?)$/.test(trimmedQuery)) {
            let parts
            if (trimmedQuery.indexOf(",") > -1) {
                parts = trimmedQuery.split(",")
            } else if (trimmedQuery.indexOf(" ") > -1) {
                parts = trimmedQuery.split(" ")
            }
            parts = parts.map(part => part.trim())

            if (isFinite(parts[0]) && Math.abs(parts[0]) <= 90
                && isFinite(parts[1]) && Math.abs(parts[1]) <= 180) {
                return {
                    lat: Number(parts[0]),
                    lng: Number(parts[1])
                }
            }
        } else if (/^([0-9]{1,2})[:|°]([0-9]{1,2})[:|'|′]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″]([N|S])(,?)(\s+)([0-9]{1,3})[:|°]([0-9]{1,2})[:|'|′]?([0-9]{1,2}(?:\.[0-9]+){0,1})?["|″]([E|W])$/.test(trimmedQuery)) {
            try {
                const coords: { lat: number, lon: number } = parseDms(trimmedQuery)
                return {
                    lat: coords.lat,
                    lng: coords.lon
                }
            } catch (e) {
                // it's not coordinates, skipping
            }
        }

        try {
            const response = await geocode(trimmedQuery)
            const { lat, lng } = response.results[0].geometry.location
            return {
                lat,
                lng
            }
        } catch (e) {
            console.log(e)
        }
        return null
    }

    static async getAddressByCoordinates(
        coordinates: Coordinates,
        options?: { fillMissingCityFromSubdivision?: boolean, formatUnitedStatesAddress?: boolean }
    ): Promise<{ address: Address | null, isoAlpha2CountryCode: string | null, cityProvenance: CityProvenance }> {
        try {
            const response = await geocode(`${coordinates.lat} ${coordinates.lng}`)

            const result = response.results[0]
            if (!result) {
                return {
                    address: null,
                    isoAlpha2CountryCode: null,
                    cityProvenance: "none",
                }
            }

            const streetName = (result.address_components.find(ac => ac.types.indexOf("route") > -1))?.long_name || ""
            const streetNumber = (result.address_components.find(ac => ac.types.indexOf("street_number") > -1))?.long_name || ""
            const country = result.address_components.find(ac => ac.types.indexOf("country") > -1)
            const cityComponents = options?.fillMissingCityFromSubdivision
                ? response.results.flatMap(r => r.address_components)
                : result.address_components
            const { city, provenance: cityProvenance } = resolveGeocodedCity(cityComponents, options)
            const isUnitedStates = !!options?.formatUnitedStatesAddress && isUnitedStatesCountry(country)
            const formattedCity = isUnitedStates ? appendUnitedStatesStateCode(city, cityComponents) : city

            return {
                address: new Address({
                    street: formatGeocodedStreet(streetName, streetNumber, isUnitedStates),
                    zip: (result.address_components.find(ac => ac.types.indexOf("postal_code") > -1))?.long_name || undefined,
                    city: formattedCity || undefined,
                    countryName: country?.long_name || undefined,
                }),
                isoAlpha2CountryCode: country?.short_name || null,
                cityProvenance,
            }
        } catch (e) {
            console.log(e)
        }
        return {
            address: null,
            isoAlpha2CountryCode: null,
            cityProvenance: "none",
        }
    }

    static async getCurrentLocation(): Promise<Coordinates | null> {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(position => {
                    resolve({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    })
                }, function () {
                    resolve(null)
                })
            } else {
                resolve(null)
            }
        })
    }

    static decimalCoordinatesToDegreesString(decimalCoordinates: Coordinates): string | null {
        if (decimalCoordinates.lat === 0 && decimalCoordinates.lng === 0) {
            return null
        }
        const coords = formatcoords(decimalCoordinates)
        return coords.format("DDMMssX")
    }


    static async checkIfIsMaxZoomAtPointForMapType(zoom: number, point: google.maps.LatLng | google.maps.LatLngLiteral, mapType: MapType) {
        return new Promise((resolve, reject) => {
            if (mapType === MapType.roadmap) {
                resolve(zoom >= 22)
                return
            }
            new google.maps.MaxZoomService().getMaxZoomAtLatLng(point, (maxZoomResult: google.maps.MaxZoomResult) => {
                if (maxZoomResult.status !== google.maps.MaxZoomStatus.OK) {
                    return reject()
                }
                resolve(zoom >= maxZoomResult.zoom)
            })
        })
    }

    static getDistanceBetweenCoordinates(first: Coordinates, second: Coordinates) {
        const R = 6371e3; // metres
        const phi1 = first.lat * Math.PI / 180 // Phi, Lambda in radians
        const phi2 = second.lat * Math.PI / 180
        const deltaPhi = (second.lat - first.lat) * Math.PI / 180
        const deltaLambda = (second.lng - first.lng) * Math.PI / 180

        const a =
            Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
            Math.cos(phi1) * Math.cos(phi2) *
            Math.sin(deltaLambda / 2) * Math.sin(deltaLambda / 2)

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

        const d = R * c // in metres

        return d
    }

    static isLocationInGeofenceRadius({ center, radius, location }: { center: Coordinates, radius: number, location: Coordinates }): boolean {
        return LocationService.getDistanceBetweenCoordinates(center, location) <= radius
    }
}

